import React, { useEffect, useState } from 'react';
import { Play } from 'lucide-react';
import { parseVideoUrl } from '../../lib/videos';
import { useVideoThumbnail } from '../../lib/useVideoThumbnail';

interface VideoPlayerProps {
    url: string;
    title: string;
    /** Thumbnail override — otherwise the provider's own thumbnail is used. */
    poster?: string;
    className?: string;
    /** Start with the iframe already mounted instead of the click-to-play poster. */
    autoMount?: boolean;
}

/**
 * 16:9 player with a click-to-play facade: the third-party iframe is only
 * mounted once the viewer asks for it, so YouTube/Loom load nothing (and set
 * no cookies) on page load.
 */
const VideoPlayer: React.FC<VideoPlayerProps> = ({ url, title, poster, className = '', autoMount = false }) => {
    const video = parseVideoUrl(url);
    const thumb = useVideoThumbnail(url, poster);
    const [playing, setPlaying] = useState(autoMount);

    // Swapping to a different video in the playlist resets the facade.
    useEffect(() => {
        setPlaying(autoMount);
    }, [url, autoMount]);

    if (!video) return null;

    return (
        <div className={`relative aspect-video overflow-hidden rounded-2xl card-surface ${className}`}>
            {playing ? (
                <iframe
                    src={video.embedUrl}
                    title={title}
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                />
            ) : (
                <button
                    type="button"
                    onClick={() => setPlaying(true)}
                    aria-label={`Play ${title}`}
                    className="group absolute inset-0 w-full h-full text-left"
                >
                    {thumb.src ? (
                        <img
                            src={thumb.src}
                            alt=""
                            loading="lazy"
                            onError={thumb.onError}
                            onLoad={thumb.onLoad}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                        />
                    ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/25 via-navy-800 to-brand-cyan/15" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-950/90 via-navy-950/30 to-navy-950/10" />

                    {/* play control */}
                    <span className="absolute inset-0 grid place-items-center">
                        <span className="grid place-items-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/95 text-navy-900 shadow-lift transition-transform duration-300 group-hover:scale-110">
                            <Play className="w-6 h-6 sm:w-7 sm:h-7 translate-x-0.5 fill-navy-900" />
                        </span>
                    </span>

                    <span className="absolute top-4 left-4 rounded-full glass px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">
                        {video.label}
                    </span>
                    <span className="absolute bottom-4 inset-x-4 font-display font-semibold text-white text-base sm:text-lg leading-snug line-clamp-2">
                        {title}
                    </span>
                </button>
            )}
        </div>
    );
};

export default VideoPlayer;
